import { useEffect, useMemo, useRef, useState } from "react";
import { isAxiosError } from "axios";
import { toast } from "sonner";
import ConfirmDialog from "ui/ConfirmDialog";
import { getAssignedTourAtraaf, deleteAssignedTourAtraaf } from "services/tourApprovalService";
import {
  ArrowUpDown,
  CalendarDays,
  // FileSpreadsheet,
  MapPinned,
  Search,
  Plus,
  Trash2,
  Loader2,
  FileDown,
} from "lucide-react";
import { usePageHeader } from "../../contexts/PageHeaderContext";
import { FullScreenLoading } from "components/ui/loader";
import { getVenues, VenueApi } from "services/tourService";
import { getAtraafRouteOptions, AtraafRouteOption } from "services/ziyaratService";
import { getAtraafRoutePlans, AtraafRoutePlan, AtraafRoutePlanDay } from "services/ziyaratService";
import { exportAtraafRoutes, assignTourAtraafs, getAtraafAssignment, AtraafAssignmentData } from "services/ziyaratService";
import useDebounce from "hooks/useDebounce";
import Pagination from "ui/pagination";
import { Button } from "../../ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "../../ui/dialog";
import { Input } from "../../ui/input";
import { Label } from "../../ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../ui/select";

const capacityColor = (count: number, capacity: number | null) => {
  if (!count) return "text-slate-300";
  if (capacity === null || capacity <= 0) return "text-slate-500";
  if (count >= capacity) return "text-red-400";
  if (count / capacity >= 0.75) return "text-amber-500";
  return "text-emerald-500";
};

const colorFor = (day: AtraafRoutePlanDay | undefined, capacity: number | null) => {
  if (!day || !day.isAvailable) return "text-slate-400";
  if (!day.applicantCount) return "text-slate-300";
  const status = day.status.toLowerCase().replace(/[^a-z]/g, "");
  if (status === "capacitynotconfigured" || capacity === null || capacity <= 0) return "text-slate-500";
  if (["full", "overbooked", "fulloverbooked"].includes(status)) return "text-red-400";
  if (status === "nearcapacity") return "text-amber-500";
  if (status === "open") return "text-emerald-500";
  return capacityColor(day.applicantCount, capacity);
};

export function AtraafRoutePlannerPage() {
  const { setPageHeader } = usePageHeader();
  const [selected, setSelected] = useState<{ routeId: string; date: string } | null>(null);
  const [tourSearch, setTourSearch] = useState("");
  const [tourDetails, setTourDetails] = useState<AtraafAssignmentData | null>(null);
  const [tourDetailsLoading, setTourDetailsLoading] = useState(false);
  const [tourDetailsError, setTourDetailsError] = useState("");
  const [assigningTourId, setAssigningTourId] = useState<string | null>(null);
  const assignmentPending = useRef(false);
  const [deleteTarget, setDeleteTarget] = useState<AtraafAssignmentData["assignedTours"][number] | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [exporting, setExporting] = useState(false);
  const exportPending = useRef(false);
  const [plansRefresh, setPlansRefresh] = useState(0);
  const [tourDetailsRetry, setTourDetailsRetry] = useState(0);
  const popupDateRef = useRef<HTMLInputElement>(null);
  const [routeSearch, setRouteSearch] = useState("");
  const debouncedSearch = useDebounce(routeSearch, 400);
  const [plans, setPlans] = useState<AtraafRoutePlan[]>([]);
  const [plansLoading, setPlansLoading] = useState(false);
  const [plansError, setPlansError] = useState("");
  const [filterRoute, setFilterRoute] = useState("all");
  const [loadStatus, setLoadStatus] = useState("all");
  const [fromDate, setFromDate] = useState(() => {
    const today = new Date();
    return `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, "0")}-${String(today.getDate()).padStart(2, "0")}`;
  });
  const [weekCount, setWeekCount] = useState(1);
  const [venue, setVenue] = useState("");
  const [venues, setVenues] = useState<VenueApi[]>([]);
  const [venuesLoading, setVenuesLoading] = useState(true);
  const [venuesError, setVenuesError] = useState("");
  const [routeOptions, setRouteOptions] = useState<AtraafRouteOption[]>([]);
  const [routesLoading, setRoutesLoading] = useState(false);
  const loading = venuesLoading || routesLoading || plansLoading;
  const [routesError, setRoutesError] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [routeSort, setRouteSort] = useState<"route" | "pax">("route");
  const [routeSortDirection, setRouteSortDirection] = useState<"asc" | "desc">("asc");
  const fromDateRef = useRef<HTMLInputElement>(null);
  const selectedVenueId = venues.find((item) => item.publicId === venue)?.venueId;

  useEffect(() => {
    let active = true;
    setRouteOptions([]);
    setRoutesError("");
    setFilterRoute("all");
    if (!selectedVenueId) {
      setRoutesLoading(false);
      return;
    }

    setRoutesLoading(true);
    const loadRoutes = async () => {
      try {
        const data = await getAtraafRouteOptions(selectedVenueId);
        if (active) setRouteOptions(data);
      } catch {
        if (active) setRoutesError("Failed to load routes. Please select the venue again to retry.");
      } finally {
        if (active) setRoutesLoading(false);
      }
    };
    loadRoutes();
    return () => {
      active = false;
    };
  }, [selectedVenueId]);

  useEffect(() => {
    let active = true;

    const loadVenues = async () => {
      try {
        const data = await getVenues();
        if (active) setVenues(data);
      } catch {
        if (active) setVenuesError("Failed to load venues. Please reload the page to try again.");
      } finally {
        if (active) setVenuesLoading(false);
      }
    };

    loadVenues();
    return () => {
      active = false;
    };
  }, []);

  const dates = useMemo(() => {
    const start = new Date(`${fromDate}T00:00:00`);
    if (Number.isNaN(start.getTime())) return [];
    return Array.from({ length: weekCount * 7 }, (_, index) => {
      const date = new Date(start);
      date.setDate(date.getDate() + index);
      return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;
    });
  }, [fromDate, weekCount]);

  const toDate = dates[dates.length - 1] || fromDate;
  useEffect(() => {
    setPageHeader({
      title: "Atraaf Route Planner",
      icon: <MapPinned className="h-4 w-4" />,
    });
    return () => setPageHeader(null);
  }, [setPageHeader]);

  useEffect(() => {
    setSelected(null);
    setPlans([]);
  }, [venue, filterRoute, loadStatus, debouncedSearch, fromDate, toDate]);

  useEffect(() => {
    let active = true;
    setPlansError("");
    if (!venue || !fromDate || !toDate) {
      setPlansLoading(false);
      return;
    }
    setPlansLoading(true);
    const loadPlans = async () => {
      try {
        const data = await getAtraafRoutePlans({
          VenuePublicId: venue,
          AtraafPublicId: filterRoute === "all" ? undefined : filterRoute,
          LoadStatus: loadStatus === "all" ? undefined : loadStatus,
          Search: debouncedSearch.trim() || undefined,
          FromDate: fromDate,
          ToDate: toDate,
        });
        if (active) setPlans(data.routes);
      } catch {
        if (active) setPlansError("Failed to load route plans. Please reload the page to try again.");
      } finally {
        if (active) setPlansLoading(false);
      }
    };
    loadPlans();
    return () => { active = false; };
  }, [venue, filterRoute, loadStatus, debouncedSearch, fromDate, toDate, plansRefresh]);

  const visibleRoutes = [...plans].sort((left, right) => {
    const result = routeSort === "route"
      ? left.atraafName.localeCompare(right.atraafName)
      : (left.capacity ?? -1) - (right.capacity ?? -1);
    return result * (routeSortDirection === "asc" ? 1 : -1);
  });

  useEffect(() => {
    setCurrentPage(1);
  }, [
    routeSearch,
    filterRoute,
    loadStatus,
    venue,
    fromDate,
    weekCount,
    routeSort,
    routeSortDirection,
  ]);

  const page = Math.min(
    currentPage,
    Math.max(1, Math.ceil(visibleRoutes.length / itemsPerPage)),
  );

  const paginatedRoutes = visibleRoutes.slice(
    (page - 1) * itemsPerPage,
    page * itemsPerPage,
  );

  const route = plans.find((item) => item.atraafPublicId === selected?.routeId);
  const selectedDay = route?.days.find((day) => day.date.slice(0, 10) === selected?.date);
  const selectedTotal = selectedDay?.applicantCount ?? 0;
  const matchesTourSearch = (tour: AtraafAssignmentData["assignedTours"][number]) =>
    `${tour.tourCode} ${tour.tourName}`.toLowerCase().includes(tourSearch.trim().toLowerCase());
  const assigned = (tourDetails?.assignedTours ?? []).filter(matchesTourSearch);
  const candidates = (tourDetails?.notAssignedTours ?? []).filter(matchesTourSearch);

  useEffect(() => {
    let active = true;
    setTourDetails(null);
    setTourDetailsError("");
    if (!selected?.routeId || !selected.date || !venue) {
      setTourDetailsLoading(false);
      return;
    }
    setTourDetailsLoading(true);
    getAtraafAssignment({
      AtraafPublicId: selected.routeId,
      VenuePublicId: venue,
      Date: selected.date,
    }).then((data) => {
      if (active) setTourDetails(data);
    }).catch(() => {
      if (active) setTourDetailsError("Failed to load tour assignments. Please try again.");
    }).finally(() => {
      if (active) setTourDetailsLoading(false);
    });
    return () => { active = false; };
  }, [selected, venue, tourDetailsRetry]);

  const showMutationError = (error: unknown, fallback: string) => {
    const response = isAxiosError<{ message?: string; errors?: string[] | Record<string, string[]> }>(error)
      ? error.response?.data
      : undefined;
    const validationMessages = response?.errors
      ? Object.values(response.errors).flat().filter((message) => typeof message === "string" && message.trim()).join("\n")
      : "";
    toast.error(
      response?.message?.trim() || validationMessages ||
      (!isAxiosError(error) && error instanceof Error ? error.message : fallback),
    );
  };

  const handleDelete = async () => {
    if (!deleteTarget || !selected?.date || !venue || assignmentPending.current) return;
    assignmentPending.current = true;
    setDeleting(true);
    try {
      const assignments = await getAssignedTourAtraaf(deleteTarget.tourPublicId);
      if (!assignments.success) {
        throw new Error(assignments.message || assignments.errors?.join("\n") || "Failed to load the assignment.");
      }
      const matches = assignments.data.assignments.filter((item) =>
        item.tourPublicId === deleteTarget.tourPublicId &&
        item.atraafPublicId === selected.routeId &&
        item.venuePublicId === venue &&
        item.venueDate.slice(0, 10) === selected.date,
      );
      if (matches.length !== 1 || !matches[0].publicId) {
        throw new Error("Could not identify this assignment. Please reload the tours and try again.");
      }
      const response = await deleteAssignedTourAtraaf(matches[0].publicId);
      if (!response.success) {
        throw new Error(response.message || response.errors?.join("\n") || "Failed to delete assignment.");
      }
      setDeleteTarget(null);
      toast.success(response.message || "Assignment deleted successfully.");
      setTourDetails(null);
      setTourDetailsLoading(true);
      setTourDetailsRetry((value) => value + 1);
      setPlansRefresh((value) => value + 1);
    } catch (error) {
      showMutationError(error, "Failed to delete assignment. Please try again.");
    } finally {
      assignmentPending.current = false;
      setDeleting(false);
    }
  };

  const handleAssign = async (tourPublicId: string) => {
    if (!selected?.date || !venue || assignmentPending.current) return;
    assignmentPending.current = true;
    setAssigningTourId(tourPublicId);
    try {
      await assignTourAtraafs(tourPublicId, {
        venuePublicId: venue,
        venueDate: selected.date,
        atraafPublicIds: [selected.routeId],
      });
      setTourDetails(null);
      setTourDetailsLoading(true);
      setTourDetailsRetry((value) => value + 1);
      setPlansRefresh((value) => value + 1);
    } catch (error) {
      showMutationError(error, "Failed to assign tour. Please try again.");
    } finally {
      assignmentPending.current = false;
      setAssigningTourId(null);
    }
  };

  const changeRouteSort = (key: "route" | "pax") => {
    setRouteSortDirection((current) =>
      routeSort === key && current === "asc" ? "desc" : "asc",
    );
    setRouteSort(key);
  };
  
  const downloadExcel = async () => {
    if (!venue || !fromDate || !toDate || exportPending.current) return;
    exportPending.current = true;
    setExporting(true);
    try {
      const blob = await exportAtraafRoutes({
        startDate: fromDate,
        endDate: toDate,
        venuePublicId: venue,
      });
      if (blob.type.includes("json")) {
        const response = JSON.parse(await blob.text());
        throw new Error(response.message || "Failed to download route data. Please try again.");
      }
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `atraaf-route-plans-${fromDate}-to-${toDate}.xlsx`;
      document.body.appendChild(link);
      try {
        link.click();
      } finally {
        link.remove();
        window.setTimeout(() => URL.revokeObjectURL(url), 1000);
      }
    } catch (error) {
      // Blob responses also wrap JSON validation errors from the export endpoint.
      if (isAxiosError(error) && error.response?.data instanceof Blob) {
        try {
          error.response.data = JSON.parse(await error.response.data.text());
        } catch {
          // Keep the fallback toast when the server does not return JSON.
        }
      }
      showMutationError(error, "Failed to download route data. Please try again.");
    } finally {
      exportPending.current = false;
      setExporting(false);
    }
  };

  return (
    <div className="space-y-3">
      {loading && <FullScreenLoading />}
      <div className="grid items-end gap-3 sm:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-[minmax(150px,1fr)_minmax(190px,1fr)_minmax(140px,1fr)_minmax(160px,1fr)_minmax(160px,1fr)_minmax(220px,1.4fr)]">
        <label className="text-xs font-semibold leading-4 text-slate-700">
          From
          <div className="relative mt-1">
            <Input
              ref={fromDateRef}
              type="date"
              value={fromDate}
              onChange={(e) => {
                setFromDate(e.target.value);
              }}
              className="h-10 bg-white pr-10 text-sm [&::-webkit-calendar-picker-indicator]:hidden"
            />
            <button
              type="button"
              aria-label="Open from date calendar"
              onClick={() => fromDateRef.current?.showPicker()}
              className="absolute right-0 top-0 flex h-10 w-10 items-center justify-center"
            >
              <CalendarDays className="h-5 w-5 text-slate-600" />
            </button>
          </div>
        </label>
        <fieldset className="min-w-0 text-xs font-semibold leading-4 text-slate-700">
          <legend>Duration</legend>
          <div className="flex h-10 items-center gap-4">
            {[1, 2].map((weeks) => (
              <label
                key={weeks}
                className="flex items-center gap-2 whitespace-nowrap"
              >
                <input
                  type="radio"
                  name="planner-duration"
                  value={weeks}
                  checked={weekCount === weeks}
                  onChange={() => {
                    setWeekCount(weeks);
                  }}
                  className="accent-[#5B9BD5]"
                />
                {weeks === 1 ? "One Week" : "Two Week"}
              </label>
            ))}
          </div>
        </fieldset>
        <div className="space-y-1">
          <Label
            htmlFor="planner-venue"
            className="text-xs font-semibold leading-4 text-slate-700"
          >
            Venue <span className="text-red-500" aria-hidden="true">*</span>
          </Label>
          <Select
            value={venue}
            required
            disabled={venuesLoading || venues.length === 0}
            onValueChange={(value) => {
              setVenue(value);
              setFilterRoute("all");
              setLoadStatus("all");
            }}
          >
            <SelectTrigger id="planner-venue" className="h-10 bg-white">
              <SelectValue placeholder={venuesLoading ? "Loading venues..." : "Select venue"} />
            </SelectTrigger>
            <SelectContent>
              {venues.map((item) => (
                <SelectItem key={item.venueId} value={item.publicId}>
                  {item.venueName}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {venuesError ? (
            <p className="text-xs text-red-500" role="alert">{venuesError}</p>
          ) : !venuesLoading && venues.length === 0 ? (
            <p className="text-xs text-slate-500">No active venues available.</p>
          ) : null}
        </div>
        <div className="space-y-1">
          <Label htmlFor="filter-route" className="text-xs font-semibold leading-4 text-slate-700">
            Route
          </Label>
          <Select value={filterRoute} onValueChange={setFilterRoute} disabled={!venue || routesLoading || routeOptions.length === 0}>
            <SelectTrigger
              id="filter-route"
              className="h-10 bg-white text-sm"
            >
              <SelectValue placeholder="All routes">{routesLoading ? "Loading routes..." : undefined}</SelectValue>
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All routes</SelectItem>
              {routeOptions.map((r) => (
                <SelectItem key={r.atraafId} value={r.publicId}>
                  {r.atraafName}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {routesError ? (
            <p className="text-xs text-red-500" role="alert">{routesError}</p>
          ) : venue && !routesLoading && routeOptions.length === 0 ? (
            <p className="text-xs text-slate-500">No active routes available for this venue.</p>
          ) : null}
        </div>
        <div className="space-y-1">
          <Label htmlFor="filter-load-status" className="text-xs font-semibold leading-4 text-slate-700">
            Load Status
          </Label>
          <Select value={loadStatus} onValueChange={setLoadStatus} disabled={!venue}>
            <SelectTrigger
              id="filter-load-status"
              className="h-10 bg-white text-sm"
            >
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All statuses</SelectItem>
              <SelectItem value="Available">Open</SelectItem>
              <SelectItem value="OverCapacity">Near capacity</SelectItem>
              <SelectItem value="Full">Full / overbooked</SelectItem>
              <SelectItem value="Unavailable">No requests</SelectItem>
              <SelectItem value="CapacityNotConfigured">Capacity not configured</SelectItem>
              {/* "LoadStatus must be Available, Full, OverCapacity, Unavailable, or CapacityNotConfigured." */}
            </SelectContent>
          </Select>
        </div>
        <div className="relative">
          <Search className="absolute left-3 top-2.5 h-5 w-5 text-slate-400" />
          <Input
            value={routeSearch}
            disabled={!venue}
            onChange={(e) => setRouteSearch(e.target.value)}
            placeholder="Passenger Name/Tour ID/Tour Name"
            className="h-10 bg-white pl-10 text-sm"
          />
        </div>
      </div>

      <div className="overflow-hidden rounded-xl border border-sky-200 bg-white shadow-sm">
        <div className="flex flex-wrap items-center justify-between gap-3 border-b px-4 py-3 text-xs">
          <span className="font-medium text-slate-700">
            Showing {paginatedRoutes.length} of {visibleRoutes.length} Routes
          </span>
          <div className="flex flex-wrap gap-4 text-slate-500">
            {[
              ["bg-emerald-500", "Open"],
              ["bg-amber-500", "Near Capacity"],
              ["bg-red-400", "Full / Overbooked"],
              ["bg-slate-200", "No Requests"],
              ["bg-slate-500", "Capacity Not Configured"],
            ].map(([c, l]) => (
              <span key={l} className="flex items-center gap-1.5">
                <i className={`h-2.5 w-2.5 rounded-full ${c}`} />
                {l}
              </span>
            ))}
          </div>
          <div className="flex gap-4">
            <button
              type="button"
              title="Download Atraaf routes for the selected venue and date range"
              aria-label={exporting ? "Downloading Atraaf routes" : "Download Atraaf routes in Excel"}
              aria-busy={exporting}
              onClick={downloadExcel}
              disabled={!venue || !fromDate || !toDate || exporting}
              className="text-slate-600 hover:text-emerald-600 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {exporting ? <Loader2 className="h-5 w-5 animate-spin" aria-hidden="true" /> : <FileDown className="h-5 w-5" />}
            </button>
          </div>
        </div>
        {venue && !plansLoading && !plansError && (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[1110px] border-separate border-spacing-0 text-sm">
              <thead className="bg-sky-50">
                <tr>
                  <th className="sticky left-0 z-20 min-w-[220px] border-b bg-sky-50 px-3 py-2 text-left text-[#5B9BD5]">
                    <button
                      type="button"
                      onClick={() => changeRouteSort("route")}
                      className="flex items-center gap-1 font-semibold"
                    >
                      Route
                      <ArrowUpDown
                        className={`h-3 w-3 ${routeSort === "route" ? "text-sky-700" : "text-sky-400"}`}
                      />
                    </button>
                  </th>
                  <th className="sticky left-[220px] z-20 w-14 border-b border-r bg-sky-50 px-2 py-2 text-center text-[#5B9BD5]">
                    <button
                      type="button"
                      onClick={() => changeRouteSort("pax")}
                      className="mx-auto flex items-center gap-1 font-semibold"
                    >
                      Pax
                      <ArrowUpDown
                        className={`h-3 w-3 ${routeSort === "pax" ? "text-sky-700" : "text-sky-400"}`}
                      />
                    </button>
                  </th>
                  {dates.map((d) => {
                    const x = new Date(`${d}T00:00:00`);
                    return (
                      <th
                        key={d}
                        className="min-w-[55px] border-b px-1 py-2 text-center text-xs font-semibold text-[#5B9BD5]"
                      >
                        {String(x.getDate()).padStart(2, "0")}
                        <span className="block">
                          {x.toLocaleDateString("en-US", { month: "short" })}
                        </span>
                      </th>
                    );
                  })}
                </tr>
              </thead>
              <tbody>
                {paginatedRoutes.map((r) => (
                  <tr key={r.atraafPublicId}>
                    <td className="sticky left-0 z-10 border-b bg-white px-3 py-2.5 text-xs font-semibold text-slate-700">
                      {r.atraafName}
                    </td>
                    <td className="sticky left-[220px] z-10 border-b border-r bg-white px-2 py-2.5 text-center text-xs text-slate-600">
                      {r.capacity ?? "—"}
                    </td>
                    {dates.map((d) => {
                      const day = r.days.find((item) => item.date.slice(0, 10) === d);
                      return (
                        <td key={d} className="border-b px-1 py-2.5 text-center">
                          <button
                            type="button"
                            onClick={() => {
                              setSelected({ routeId: r.atraafPublicId, date: d });
                              setTourSearch("");
                            }}
                            aria-label={`View ${r.atraafName} on ${d}`}
                            title={day ? `${day.status}${day.isAvailable ? "" : " — Unavailable"}` : "No data"}
                            className={`w-full rounded text-sm font-semibold hover:bg-sky-50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-sky-500 ${colorFor(day, r.capacity)}`}
                          >
                            {day ? day.applicantCount || "–" : "—"}
                          </button>
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {!venue ? (
          <div className="py-8 text-center text-sm text-red-500" role="status">
            Please select a venue to view routes.
          </div>
        ) : !fromDate ? (
          <div className="py-8 text-center text-sm text-slate-500">Please select a start date.</div>
        ) : plansLoading ? null : plansError ? (
          <div className="py-8 text-center text-sm text-red-500" role="alert">{plansError}</div>
        ) : visibleRoutes.length > 0 ? (
          // Temporarily hide pagination while keeping it available for later.
          <div hidden>
            <Pagination
              totalItems={visibleRoutes.length}
              itemsPerPage={itemsPerPage}
              currentPage={page}
              onPageChange={setCurrentPage}
              onPageSizeChange={(size) => {
                setItemsPerPage(size);
                setCurrentPage(1);
              }}
              label="routes"
            />
          </div>
        ) : (
          <div className="py-8 text-center text-sm text-slate-500">
            No routes match the selected filters.
          </div>
        )}
      </div>

      <Dialog
        open={!!selected}
        onOpenChange={(open) => !open && !assignmentPending.current && !deleteTarget && setSelected(null)}
      >
        <DialogContent className="!w-[88vw] !max-w-[800px] max-h-[90vh] overflow-hidden bg-white p-5">
          <DialogHeader>
            <DialogTitle className="text-xl text-slate-800">
              {route?.atraafName}
            </DialogTitle>
            <DialogDescription className="sr-only">
              View and manage tour assignments for the selected route and date.
            </DialogDescription>
          </DialogHeader>
          <div className="flex items-center gap-8">
            <div className="relative w-64">
              <Input
                ref={popupDateRef}
                disabled={!!assigningTourId || deleting || !!deleteTarget}
                type="date"
                value={selected?.date || ""}
                min={fromDate}
                max={toDate}
                onChange={(e) => {
                  if (!selected) return;
                  setTourDetails(null);
                  setTourDetailsError("");
                  setTourDetailsLoading(!!e.target.value);
                  setSelected({ ...selected, date: e.target.value });
                }}
                className="h-10 pr-10 text-sm [&::-webkit-calendar-picker-indicator]:hidden"
              />
              <button
                type="button"
                aria-label="Open route assignment date calendar"
                disabled={!!assigningTourId || deleting || !!deleteTarget}
                onClick={() => popupDateRef.current?.showPicker()}
                className="absolute right-0 top-0 flex h-10 w-10 items-center justify-center"
              >
                <CalendarDays className="h-5 w-5 text-slate-600" />
              </button>
            </div>
            <div className="text-base">
              <b className={colorFor(selectedDay, route?.capacity ?? null)}>
                {selectedTotal}
              </b>{" "}
              <b>/ {route?.capacity ?? "—"}</b>
              <span className="ml-2">Applicants</span>
            </div>
          </div>
          <div className="relative">
            <Search className="absolute left-3 top-2.5 h-5 w-5 text-slate-400" />
            <Input
              value={tourSearch}
              onChange={(e) => setTourSearch(e.target.value)}
              placeholder="Search by Tour Name / ID"
              className="h-10 pl-10 text-sm"
            />
          </div>
          {!selected?.date && <p className="text-sm text-red-500" role="status">Please select a date to view tours.</p>}
          {tourDetailsLoading && (
            <div className="flex min-h-[200px] flex-col items-center justify-center gap-3 text-[#5B9BD5]" role="status" aria-live="polite">
              <Loader2 className="h-8 w-8 animate-spin" aria-hidden="true" />
              <p className="text-sm font-medium">Loading tour assignments...</p>
            </div>
          )}
          {tourDetailsError && (
            <div role="alert" className="flex items-center gap-3 text-sm text-red-500">
              {tourDetailsError}
              <Button variant="outline" size="sm" onClick={() => setTourDetailsRetry((value) => value + 1)}>Retry</Button>
            </div>
          )}
          {tourDetails && !tourDetailsLoading && !tourDetailsError && <div className="max-h-[calc(90vh-190px)] space-y-4 overflow-y-auto pr-1">
            <section>
              <h3 className="mb-2 text-sm font-bold text-slate-700">
                Assigned Tours
              </h3>
              <div className="space-y-2">
                {assigned.length === 0 && <p className="text-sm text-slate-500">{tourSearch.trim() ? "No assigned tours match your search." : "No assigned tours for this date."}</p>}
                {assigned.map((t) => {
                  return (
                    <div
                      key={t.tourPublicId}
                      className="grid grid-cols-[minmax(0,1fr)_76px_165px_36px] items-center gap-2 rounded-lg border border-sky-200 p-2.5"
                    >
                      <div className="min-w-0">
                        <div className="truncate text-[11px] font-semibold text-slate-500">
                          {t.tourCode}
                        </div>
                        <div className="truncate text-sm text-slate-700">
                          {t.tourName}
                        </div>
                      </div>
                      <Input
                        type="number"
                        disabled
                        min={1}
                        value={t.noOfApplicants}
                        className={`!h-9 !w-[76px] px-2 text-base font-semibold ${capacityColor(t.noOfApplicants, route?.capacity ?? null)}`}
                      />
                      <Input
                        type="date"
                        disabled
                        value={selected?.date || ""}
                        min={dates[0]}
                        max={dates[dates.length - 1]}
                        className="!h-9 !w-[165px] px-2 text-xs"
                      />
                      <Button
                        disabled={!!assigningTourId || deleting || tourDetailsLoading}
                        onClick={() => setDeleteTarget(t)}
                        aria-label={`Delete assignment for ${t.tourName}`}
                        size="sm"
                        variant="ghost"
                        className="h-8 w-8 p-0"
                      >
                        <Trash2 className="h-4 w-4 text-red-400" />
                      </Button>
                    </div>
                  );
                })}
              </div>
            </section>
            <section>
              <h3 className="mb-2 text-sm font-bold text-slate-700">
                Not Assigned Tours
              </h3>
              <div className="overflow-hidden rounded-lg border border-sky-200">
                {candidates.length === 0 && <p className="p-3 text-sm text-slate-500">{tourSearch.trim() ? "No unassigned tours match your search." : "No unassigned tours for this date."}</p>}
                {candidates.map((t, i) => (
                  <div
                    key={t.tourPublicId}
                    className={`grid grid-cols-[minmax(0,1fr)_68px_108px] items-center gap-2 border-b px-3 py-2 last:border-0 ${i === 4 ? "bg-sky-50" : ""}`}
                  >
                    <div className="min-w-0">
                      <div className="truncate text-[11px] font-semibold text-slate-500">
                        {t.tourCode}
                      </div>
                      <div className="truncate text-sm text-slate-700">
                        {t.tourName}
                      </div>
                    </div>
                    <div
                      className={`rounded-md border bg-slate-50 px-2 py-1.5 text-center text-base font-semibold ${capacityColor(t.noOfApplicants, route?.capacity ?? null)}`}
                    >
                      {String(t.noOfApplicants).padStart(2, "0")}
                    </div>
                    <Button
                      disabled={!!assigningTourId || deleting || !!deleteTarget || tourDetailsLoading || !selected?.date || !venue}
                      onClick={() => handleAssign(t.tourPublicId)}
                      size="sm"
                      className="h-9 gap-1 bg-[#5B9BD5] px-3 text-sm"
                    >
                      {assigningTourId === t.tourPublicId ? <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" /> : <Plus className="h-4 w-4" />}
                      {assigningTourId === t.tourPublicId ? "Assigning..." : "Assign"}
                    </Button>
                  </div>
                ))}
              </div>
            </section>
          </div>}
        </DialogContent>
      </Dialog>
      <ConfirmDialog
        open={!!deleteTarget}
        onOpenChange={(open) => {
          if (!open && !assignmentPending.current) setDeleteTarget(null);
        }}
        title="Delete Assignment"
        description={`Remove ${deleteTarget?.tourName || "this tour"} from ${route?.atraafName || "this route"} on ${selected?.date || "this date"}?`}
        confirmText="Delete"
        confirmVariant="destructive"
        show={false}
        loading={deleting}
        onConfirm={handleDelete}
      />
    </div>
  );
}
